import React from 'react';

import { LUMIO_MORPH, STRANDS_CFG } from '../../../../../theme/tokens';
import { useReducedMotion } from '../../../../../hooks/useReducedMotion';
import { StrandsField } from './StrandsField';
import { Root, LogoLayer, StrandsLayer } from './LumioMorph.styles';

/** Multicolour gradient Lumio mark — same asset the Lumio chat panel uses. */
const LOGO_SRC = '/assets/lumio/lumio-mark.svg';

export type LumioMorphProps = {
  /** While false (e.g. the deploy is complete) the morph stops — the logo holds. */
  enabled?: boolean;
  /** Slot size in px (default: the deploy header's 24px). The strands flourish scales with it, keeping the
   *  same logo-to-flourish ratio, so the mark can sit inline beside small status text. */
  size?: number;
  className?: string;
};

/**
 * The header Lumio mark that periodically morphs into a "Strands" shader flourish
 * and back (a cross-fade + scale). The strands only render during their phase, so
 * the slot is a cheap static logo most of the time. Reduced motion (or `enabled`
 * false, once the deploy is done) holds the logo and never runs the shader.
 */
export const LumioMorph = ({ enabled = true, size = LUMIO_MORPH.slotSize, className }: LumioMorphProps): React.ReactElement => {
  const reduced = useReducedMotion();
  const [showStrands, setShowStrands] = React.useState(false);

  React.useEffect(() => {
    if (reduced || !enabled) {
      setShowStrands(false);
      return;
    }

    let cancelled = false;
    let timer = 0;

    // Flip to a phase, hold it, then flip to the other — logo (logoMs) ⇄ strands (strandsMs).
    const flip = (toStrands: boolean): void => {
      setShowStrands(toStrands);
      const hold = toStrands ? LUMIO_MORPH.strandsMs : LUMIO_MORPH.logoMs;
      timer = window.setTimeout(() => {
        if (!cancelled) flip(!toStrands);
      }, hold);
    };

    setShowStrands(false);
    timer = window.setTimeout(() => {
      if (!cancelled) flip(true);
    }, LUMIO_MORPH.logoMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [reduced, enabled]);

  return (
    <Root className={className} $size={size}>
      <LogoLayer $show={!showStrands}>
        {/* Multicolour gradient mark — not recoloured, so a plain <img>, no inline fetch. */}
        <img src={LOGO_SRC} alt="" />
      </LogoLayer>
      {/* Drawn strandsHeadroom times larger with the shader zoomed out by the same factor: identical strands, but
          room for the glow to fade out instead of being clipped flat at the canvas top and bottom. */}
      <StrandsLayer
        $show={showStrands}
        $size={Math.round((size * LUMIO_MORPH.strandsSize * LUMIO_MORPH.strandsHeadroom) / LUMIO_MORPH.slotSize)}
      >
        <StrandsField active={showStrands && !reduced} scale={STRANDS_CFG.scale / LUMIO_MORPH.strandsHeadroom} />
      </StrandsLayer>
    </Root>
  );
};

export default LumioMorph;
